import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { iif, of, Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { Drug } from 'src/API';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import { DrugFormat } from './drug.pipe';
import { DrugbankLookupService } from './drugbank-lookup.service';

export interface CustomDrug extends Drug {
  isCustom: boolean;
}

@Component({
  selector: 'csi-medication-lookup',
  templateUrl: './medication-lookup.component.html',
  styleUrls: ['./medication-lookup.component.scss']
})
export class MedicationLookupComponent implements OnInit {
  @Input() control: FormControl;
  @Input() values?: Drug[];
  @Input() flagDrugs = false;
  @Input() label: string;
  @Input() showDrugInformation: boolean = true;
  @Input() readOnly: boolean = false;
  @Input() formatForPDF: boolean = true;

  public searchResults$: Observable<ReadonlyArray<Drug>>;
  public typeahead = new Subject<string>();
  public loading = false;
  public notFoundMessage = this.getResponseMessage();
  public isServerErroring = false;
  public isSearchInputValid = false;
  public readonly DrugFormat = DrugFormat;
  public isCustomAllowed: boolean;

  constructor(
    private drugbankLookupService: DrugbankLookupService,
    private translocoService: TranslocoService
  ) {
    this.searchResults$ = this.typeahead.pipe(
      tap(() => (this.loading = true)),
      debounceTime(200),
      switchMap(searchInput => {
        const trimmedSearchInput = searchInput ? searchInput.trim() : ''; // Safely trim or default to an empty string
        this.isSearchInputValid = trimmedSearchInput.length >= 2;
        return iif(
          () => trimmedSearchInput.length >= 2,
          this.drugbankLookupService.searchMedication({ query: trimmedSearchInput }, true),
          of([])
        ).pipe(
          map(medications => {
            this.isServerErroring = false;
            const filteredMeds = medications
              ? this.deduplicateMedications(
                  this.removeAlreadySelectedMedications(medications, this.control.value as Drug[])
                )
              : [];

            // Set the flag if no results found
            this.isCustomAllowed = filteredMeds.length === 0 && trimmedSearchInput.length >= 2;

            this.notFoundMessage = this.getResponseMessage(null, trimmedSearchInput);
            return filteredMeds;
          }),
          catchError(err => {
            console.error(err);
            this.isServerErroring = true;
            this.notFoundMessage = this.getResponseMessage(err);
            this.loading = false;
            ErrorHandlerService.trySendingSentryError(err);
            return of([]);
          })
        );
      }),
      tap(() => (this.loading = false))
    );
  }

  ngOnInit() {
    this.control.statusChanges.subscribe(status => {
      if (this.readOnly && status !== 'DISABLED') {
        this.control.disable();
      }
    });
  }

  public addCustomDrug(name: string): CustomDrug {
    return {
      name: name,
      isCustom: true,
      dosage_form: 'dosage_form_not_specified',
      active_ingredients: [
        {
          __typename: 'DrugActiveIngredients',
          ingredient: 'ingredient_not_specified',
          strength: null,
          strength_unit: ''
        }
      ],
      routes: [
        {
          __typename: 'DrugRoute',
          route_of_administration: 'route_not_specified'
        }
      ],
      __typename: 'Drug'
    };
  }

  public removeMedication(medication: Drug) {
    const currentValue: Drug[] = [...this.control.value];
    const index = currentValue.findIndex(med => JSON.stringify(med) === JSON.stringify(medication));

    if (index !== -1) {
      currentValue.splice(index, 1);
      this.control.setValue(currentValue);
    }
  }

  private getResponseMessage(err?: any, searchInput?: string) {
    if (err) {
      if (err.status === 0) {
        return this.translocoService.translate('serverNoResponse');
      } else if (400 <= err.status && err.status < 500) {
        return (
          this.translocoService.translate('clientError') +
          ' ' +
          this.translocoService.translate('contactSupport')
        );
      } else if (500 <= err.status && err.status < 600) {
        return (
          this.translocoService.translate('serverError') +
          ' ' +
          this.translocoService.translate('contactSupport')
        );
      } else {
        return (
          this.translocoService.translate('unknownError', {
            status: err.status
          }) +
          ' ' +
          this.translocoService.translate('contactSupport')
        );
      }
    } else {
      if (searchInput && searchInput.trim().length < 2) {
        return this.translocoService.translate('minChars');
      }
      return this.translocoService.translate('noItems');
    }
  }

  private removeAlreadySelectedMedications(
    medications: Drug[],
    currentMedications: Drug[]
  ): Drug[] {
    return medications.filter(medication => !this.hasMedication(medication, currentMedications));
  }

  private hasMedication(medicineToFind: Drug, medications: Drug[]): boolean {
    return medications && !!medications.find(medication => medication.id === medicineToFind.id);
  }

  private deduplicateMedications(medications: Drug[]): Drug[] {
    const seenKeys = new Set<string>();
    return medications.filter(medication => {
      const key = this.getMedicationDedupeKey(medication);
      if (seenKeys.has(key)) {
        return false;
      }
      seenKeys.add(key);
      return true;
    });
  }

  private getMedicationDedupeKey(medication: Drug): string {
    const routes = medication.routes
      ? medication.routes
          .map(route => (route && route.route_of_administration) || '')
          .join('/')
      : '';

    const ingredients = medication.active_ingredients
      ? medication.active_ingredients
          .map(
            ingredient =>
              ingredient
                ? `${ingredient.ingredient || ''}|${
                    ingredient.strength !== null && ingredient.strength !== undefined
                      ? ingredient.strength
                      : ''
                  }|${ingredient.strength_unit || ''}`
                : ''
          )
          .join(',')
      : '';

    return [medication.name || '', medication.dosage_form || '', routes, ingredients]
      .join('::')
      .toLowerCase();
  }
}
